import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  CollectorDatasetRowStats,
  DatasetFile,
  getCollectorDatasetRowStats,
  ParsedValidationError,
  parseCollectorDatasetValidationError,
  ValidationStatus
} from '../../../services/collector-datasets.service';

const STATUS_STEPS: { label: string; icon: string }[] = [
  { label: 'Recibido',   icon: 'inbox' },
  { label: 'Procesando', icon: 'sync' },
  { label: 'Resultado',  icon: 'check_circle' },
];

@Component({
  selector: 'app-collector-dataset-detail-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './collector-dataset-detail-dialog.component.html',
  styleUrl: './collector-dataset-detail-dialog.component.scss',
})
export class CollectorDatasetDetailDialogComponent {
  readonly steps = STATUS_STEPS;
  readonly parsedError: ParsedValidationError | null;
  readonly rowStats: CollectorDatasetRowStats;
  readonly missingPreviewCount = 12;
  showAllMissing = false;

  constructor(
    public dialogRef: MatDialogRef<CollectorDatasetDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dataset: DatasetFile
  ) {
    this.parsedError = parseCollectorDatasetValidationError(dataset);
    this.rowStats = getCollectorDatasetRowStats(dataset);
  }

  get visibleMissing(): string[] {
    const all = this.parsedError?.missing ?? [];
    return this.showAllMissing ? all : all.slice(0, this.missingPreviewCount);
  }

  get errorHeadline(): string {
    if (this.parsedError && (this.parsedError.missing.length || this.parsedError.reason === 'headers_mismatch')) {
      return 'Los encabezados del archivo no coinciden con la estructura esperada del schema.';
    }
    return this.failedDescription;
  }

  get successRowsLabel(): string {
    const n = this.rowStats.loaded;
    if (typeof n !== 'number') return '';
    return `${n.toLocaleString('es-MX')} ${n === 1 ? 'fila cargada' : 'filas cargadas'}`;
  }

  get isReplaceOperation(): boolean {
    return this.dataset.type === 'full_replace' || this.dataset.type === 'replace_scope';
  }

  get hasDeletedRows(): boolean {
    return this.rowStats.deleted !== null;
  }

  get deletedRowsFormatted(): string {
    return this.rowStats.deleted !== null ? this.rowStats.deleted.toLocaleString('es-MX') : '';
  }

  get loadedRowsFormatted(): string {
    return this.rowStats.loaded !== null ? this.rowStats.loaded.toLocaleString('es-MX') : '';
  }

  get scopeFilterLabel(): string {
    const scope = this.dataset.scope;
    if (!scope || !scope.column) return '';
    const value = Array.isArray(scope.value) ? scope.value.join(', ') : String(scope.value ?? '');
    return `${scope.column} ${this.scopeOperatorSymbol(scope.operator)} ${value}`.trim();
  }

  private scopeOperatorSymbol(op?: string): string {
    switch (op) {
      case 'in': return 'en';
      case 'between': return 'entre';
      case 'gte': return '≥';
      case 'lte': return '≤';
      default: return '=';
    }
  }

  get isFailed(): boolean {
    const s = this.dataset.validation_status;
    return s === 'failed' || !!s?.startsWith('failed_');
  }

  get isDone(): boolean {
    return this.dataset.validation_status === 'validated_metadata'
      || this.dataset.validation_status === 'completed';
  }

  get currentStepIndex(): number {
    if (this.dataset.validation_status === 'validated_metadata' || this.isFailed) return 2;
    if (this.dataset.validation_status === 'completed') return 2;
    if (this.dataset.validation_status === 'processing' || this.dataset.validation_status === 'validating') return 1;
    return 0;
  }

  get statusLabel(): string {
    const s = this.dataset.validation_status;
    if (s === 'validated_metadata' || s === 'completed') return 'Completado';
    if (s === 'failed_fields')      return 'Error de configuración';
    if (s === 'failed_storage')     return 'Error de archivo';
    if (s === 'failed_validation')  return 'Error de validación';
    if (s === 'failed_loading')     return 'Error al cargar';
    if (s === 'failed')             return 'Error';
    if (s === 'validating') return 'Validando';
    if (s === 'processing') return 'Procesando';
    if (s === 'queued')     return 'En cola';
    if (s === 'uploading')  return 'Subiendo';
    return 'Recibido';
  }

  get statusClass(): string {
    const s = this.dataset.validation_status;
    if (s === 'validated_metadata' || s === 'completed') return 'header-success';
    if (this.isFailed)              return 'header-error';
    if (s === 'processing' || s === 'validating') return 'header-processing';
    if (s === 'queued' || s === 'uploading')      return 'header-queued';
    return 'header-pending';
  }

  get headerIcon(): string {
    const s = this.dataset.validation_status;
    if (this.isFailed) return 'cancel';
    if (this.isDone)   return 'check_circle';
    if (s === 'processing' || s === 'validating') return 'sync';
    if (s === 'queued' || s === 'uploading')      return 'schedule';
    return 'inbox';
  }

  get failedDescription(): string {
    const s = this.dataset.validation_status;
    if (s === 'failed_fields')     return 'Hubo un problema con la configuración del schema o el mensaje de carga.';
    if (s === 'failed_storage')    return 'El archivo no se encontró o no pudo leerse correctamente.';
    if (s === 'failed_validation') return 'El archivo no pasó la validación de formato o columnas.';
    if (s === 'failed_loading')    return 'El archivo fue válido pero ocurrió un error al cargarlo a la base de datos.';
    // status "failed" genérico sin categoría: si no hay validation_message que mostrar aparte, damos un texto de respaldo
    if (this.isFailed && !this.dataset.validation_message && !this.dataset.last_error?.message) {
      return 'Ocurrió un error al procesar el archivo.';
    }
    return '';
  }

  get senderDisplay(): string {
    if (this.dataset.created_by_name && this.dataset.created_by_email) {
      return `${this.dataset.created_by_name} · ${this.dataset.created_by_email}`;
    }
    return this.dataset.created_by_email || this.dataset.created_by_name || this.dataset.created_by || '-';
  }

  operationLabel(op?: string): string {
    if (op === 'full_replace') return 'Reemplazo completo';
    if (op === 'replace_scope') return 'Reemplazo por alcance';
    if (op === 'insert') return 'Inserción de registros';
    // valores heredados
    if (op === 'replace') return 'Reemplazo completo';
    if (op === 'update') return 'Actualización parcial';
    return op || '-';
  }

  formatDate(value: unknown): string {
    if (!value) return '-';
    let date: Date | null = null;
    if (typeof (value as any).seconds === 'number') {
      date = new Date((value as any).seconds * 1000);
    } else if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      date = Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (!date) return '-';
    return date.toLocaleDateString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  stepState(index: number): 'done' | 'active' | 'pending' | 'error' {
    if (this.isFailed && index === this.currentStepIndex) return 'error';
    if (!this.isFailed && index < this.currentStepIndex) return 'done';
    if (!this.isFailed && index === this.currentStepIndex) return 'active';
    return 'pending';
  }
}
